import React from "react";
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type { ShipmentFeedOverview } from "@services/shipments/types";

interface ShipmentSelectionListProps {
  shipments: ShipmentFeedOverview[];
  onSelect: (id: string) => void;
}

export const ShipmentSelectionList: React.FC<ShipmentSelectionListProps> = ({
  shipments,
  onSelect,
}) => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {shipments.length} shipments found for this email
      </Typography>
      <Stack spacing={1}>
        {shipments.map((shipment) => {
          const containerDisplay =
            shipment.containerNumbers.length > 0
              ? shipment.containerNumbers.length > 1
                ? `${shipment.containerNumbers[0]} (+${shipment.containerNumbers.length - 1})`
                : shipment.containerNumbers[0]
              : null;

          return (
            <Box
              key={shipment.shipmentFeedId}
              onClick={() => onSelect(shipment.shipmentFeedId)}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "primary.main",
                },
                transition: "all 0.15s",
              }}
            >
              <Stack spacing={0.25}>
                {/* POL → POD */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    {shipment.pol || "—"}
                  </Typography>
                  <ArrowForwardIcon
                    sx={{ fontSize: 12, color: "text.secondary" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    {shipment.pod || "—"}
                  </Typography>
                </Stack>

                {/* Booking Number */}
                {shipment.bookingNumber && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                  >
                    Booking #: {shipment.bookingNumber}
                  </Typography>
                )}

                {/* Master Bill of Lading */}
                {shipment.masterBillOfLading && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                  >
                    BL: {shipment.masterBillOfLading}
                  </Typography>
                )}

                {/* Container Numbers */}
                {containerDisplay &&
                  (shipment.containerNumbers.length > 1 ? (
                    <Tooltip
                      title={shipment.containerNumbers.join(", ")}
                      arrow
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          textDecoration: "underline",
                          cursor: "help",
                        }}
                      >
                        Container: {containerDisplay}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                    >
                      Container: {containerDisplay}
                    </Typography>
                  ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
